import { useEffect } from "react";

/**
 * SeoContent: injects a FAQPage JSON-LD structured data snippet
 * so Google can show FAQ rich results for the current page.
 * `id` must be unique per page.
 */
export function FaqSchema({ id, faqs }) {
  useEffect(() => {
    if (!faqs || !faqs.length) return;
    const scriptId = `ld-faq-${id}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.a,
        },
      })),
    });
    return () => {
      const n = document.getElementById(scriptId);
      if (n) n.remove();
    };
  }, [id, faqs]);
  return null;
}

/**
 * OrganizationAlt: adds an Organization LD with alternateName variations
 * so Google understands all common keyword variations refer to the same site.
 */
export function OrganizationAlt() {
  useEffect(() => {
    let el = document.getElementById("ld-org-alt");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "ld-org-alt";
      document.head.appendChild(el);
    }
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Juwai Morning Teer",
      alternateName: [
        "Juwai Teer",
        "Morning Teer Juwai",
        "Juwai Morning Teer Result",
        "Juwai Teer Morning Result",
        "Morning Juwai Teer",
        "Juwai Morning Teer Today",
      ],
      url: window.location.origin,
      potentialAction: {
        "@type": "SearchAction",
        target: `${window.location.origin}/previous-results?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
  }, []);
  return null;
}

/**
 * Reusable FAQ list UI.
 */
export function FaqList({ faqs, testIdPrefix = "faq" }) {
  return (
    <div className="mt-3 rounded-2xl bg-white border border-black/5 divide-y divide-black/5 overflow-hidden">
      {faqs.map((f, i) => (
        <details
          key={i}
          className="group px-4 py-3"
          data-testid={`${testIdPrefix}-item-${i}`}
        >
          <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
            <span className="font-display font-bold text-slate-900 text-[15px] leading-snug">
              {f.q}
            </span>
            <span className="mt-1 shrink-0 w-6 h-6 rounded-full bg-[#F04E1E]/10 text-[#F04E1E] grid place-items-center text-lg font-black group-open:rotate-45 transition-transform">
              +
            </span>
          </summary>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  );
}
